import { Link } from "wouter";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { Check, AlertCircle, Star } from "lucide-react";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateSpeakableSchema,
} from "@/lib/seo-utils";

/**
 * Use-case pages (playbook 22, step 2).
 *
 * A single property risks doorway pages here: three near-identical pages that
 * swap "families" for "groups" would differ only by keyword and would deserve to
 * be ignored. So each page below carries facts that are genuinely specific to
 * that scenario - different amenities, different constraints, different guest
 * evidence - and only scenarios with real evidence get a page at all.
 *
 * That is why there is no couples page: exactly one of the 48 approved reviews
 * is a couple stay, which is not enough to write anything substantive or honest.
 *
 * Every amenity listed is a real row in the amenities table. Every quote is a
 * real approved guest review, published with permission, wording unchanged.
 * Constraints are stated plainly rather than omitted, because a use-case page
 * that only lists upsides tells a reader nothing about fit.
 */
export interface UseCase {
  slug: string;
  eyebrow: string;
  h1: string;
  title: string;
  description: string;
  /** 40-60 word direct answer, first thing after the hero. */
  answer: string;
  reviewCount: number;
  reviewAverage: string;
  suitsHeading: string;
  suits: { label: string; detail: string }[];
  constraintsHeading: string;
  constraints: string[];
  quotes: { name: string; location: string; text: string }[];
  closing: string;
}

export const USE_CASES: Record<string, UseCase> = {
  families: {
    slug: "families",
    eyebrow: "Who it suits",
    h1: "An Oahu beach house for families",
    title: "Oahu Beach House for Families | The Kualoa Beach House",
    description:
      "A 3-bedroom beachfront vacation rental in Ka'a'awa built around family stays: crib, high chair and pack 'n play on site, a single-level layout with no stairs, a fenced yard, and the beach directly off the lanai.",
    answer:
      "Twenty-eight of our forty-eight guest reviews are family stays. The house is single-level with no stairs, the yard is fenced, and the beach is directly off the lanai rather than a drive away, so younger children stay in sight from the house. A crib, high chair and pack 'n play are already here.",
    reviewCount: 28,
    reviewAverage: "4.93",
    suitsHeading: "What is actually here for children",
    suits: [
      { label: "Crib, high chair and pack 'n play", detail: "Already in the house, so they do not travel with you or get rented on arrival." },
      { label: "Single-level home, no stairs", detail: "The whole layout is on one floor - it matters with toddlers, and with anyone who cannot manage steps." },
      { label: "Private fenced backyard", detail: "An enclosed outdoor space between the house and the sand." },
      { label: "The beach is the property", detail: "Not a walk or a drive. Sightlines from the lanai to the water are the reason several families booked." },
      { label: "Children's dinnerware, toys, books and board games", detail: "Plus a full kitchen, dishwasher and in-unit washer for the realities of a week with kids." },
      { label: "Beach gear, snorkelling gear and a kayak", detail: "Shared equipment already on site rather than a rental shop trip." },
    ],
    constraintsHeading: "What families should know before booking",
    constraints: [
      "Eight guests maximum, and it is a firm cap.",
      "Three bedrooms and two bathrooms - worth planning around with a larger family.",
      "Cooling is ceiling fans plus window and portable AC units, not central air conditioning.",
      "No pets.",
      "Check-in is 3:00 PM and check-out is 10:00 AM.",
    ],
    quotes: [
      {
        name: "Delores",
        location: "",
        text: "When looking for a beachfront house I had specific requirements. I wanted a beach right on the property so we could keep eyes on our 6 grandchildren",
      },
      {
        name: "Alex",
        location: "",
        text: "The size and layout were perfect for our extended family of seven",
      },
    ],
    closing:
      "If the group is larger or you are travelling without children, the group page covers the same house from that angle.",
  },
  groups: {
    slug: "groups",
    eyebrow: "Who it suits",
    h1: "An Oahu beach house for groups of eight",
    title: "Oahu Beach House for Groups of 8 | The Kualoa Beach House",
    description:
      "A 3-bedroom beachfront vacation rental in Ka'a'awa that sleeps eight, with two driveway spaces plus free street parking, a gas grill and an outdoor dining area on the windward coast.",
    answer:
      "Nineteen of our forty-eight guest reviews are group stays. The house sleeps eight across three bedrooms and two bathrooms, with two driveway spaces plus free street parking, a gas grill and an outdoor dining area. Long-term stays are allowed, and several groups have booked for multiple weeks.",
    reviewCount: 19,
    reviewAverage: "4.95",
    suitsHeading: "What the house gives a group",
    suits: [
      { label: "Sleeps eight across three bedrooms", detail: "One house rather than adjoining hotel rooms, with a shared living space to gather in." },
      { label: "Two driveway spaces plus free street parking", detail: "Enough for the two or three cars a group of eight usually arrives in." },
      { label: "Gas BBQ, outdoor dining area and a dining table", detail: "Group meals happen at the house instead of eight covers at a restaurant every night." },
      { label: "Full kitchen with dishwasher", detail: "Stove, oven, microwave, toaster, blender and cooking basics, plus an in-unit washer." },
      { label: "Long-term stays allowed", detail: "Multi-week bookings are welcome rather than an exception." },
      { label: "Self check-in with a keypad", detail: "People arriving on different flights let themselves in." },
    ],
    constraintsHeading: "What groups should know before booking",
    constraints: [
      "Eight guests is the maximum and it is a firm cap, not a starting point.",
      "Two bathrooms for eight people - the single most common thing to plan around.",
      "No additional visitors without permission, so this is not a venue for events or parties.",
      "Cooling is ceiling fans plus window and portable AC units, not central air conditioning.",
      "No smoking anywhere on the property.",
    ],
    quotes: [
      {
        name: "Lang",
        location: "Forest Grove, Oregon",
        text: "This is a beautiful Air BnB. We had 8 people staying and felt that we had plenty of room. The home is lovely and clean and private and the views were spectacular!",
      },
    ],
    closing:
      "If the group includes young children, the families page covers what is here for them.",
  },
};

export default function UseCasePage({ useCase }: { useCase: UseCase }) {
  const { seoSettings } = useSEOSettings();
  const other = Object.values(USE_CASES).find((u) => u.slug !== useCase.slug);

  const seoHead = useSEO({
    pageType: "use-case",
    title: useCase.title,
    description: useCase.description,
    canonical: `https://vacationrentaloahu.co/${useCase.slug}`,
    ogType: "website",
    twitterCard: "summary_large_image",
    structuredData: seoSettings
      ? [
          generateBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: useCase.h1, url: `/${useCase.slug}` },
          ]),
          generateSpeakableSchema(["h1", "h2"]),
        ]
      : undefined,
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow={useCase.eyebrow}
            title={useCase.h1}
            subtitle={`${useCase.reviewCount} of our 48 guest reviews are ${useCase.slug === "families" ? "family" : "group"} stays, averaging ${useCase.reviewAverage} out of 5.`}
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt="Beachfront ocean views at The Kualoa Beach House in Ka'a'awa, Oahu"
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-4xl">
              <Breadcrumbs
                items={[
                  { name: "Home", url: "/" },
                  { name: useCase.h1, url: `/${useCase.slug}` },
                ]}
                className="mb-14 justify-center"
              />

              <ScrollReveal>
                <p className="text-luxury-lg text-ink-soft font-light leading-relaxed text-center max-w-3xl mx-auto mb-20">
                  {useCase.answer}
                </p>
              </ScrollReveal>

              <SectionHeading eyebrow="Fit" title={useCase.suitsHeading} align="left" className="mb-10" />
              <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-20">
                {useCase.suits.map((s) => (
                  <ScrollReveal key={s.label}>
                    <div className="h-full bg-sand border border-sand-deep p-7">
                      <Check className="h-5 w-5 text-forest mb-4" aria-hidden="true" />
                      <h3 className="font-serif text-ink text-[19px] leading-tight mb-2">{s.label}</h3>
                      <p className="text-[15px] text-ink-soft font-sans leading-relaxed">{s.detail}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              <SectionHeading eyebrow="Honestly" title={useCase.constraintsHeading} align="left" className="mb-10" />
              <ScrollReveal>
                <ul className="mb-20 border border-sand-deep bg-shell divide-y divide-sand-deep">
                  {useCase.constraints.map((c) => (
                    <li key={c} className="flex gap-4 items-start p-6">
                      <AlertCircle className="h-5 w-5 text-driftwood shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-[15px] text-ink-soft font-sans leading-relaxed">{c}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>

              <SectionHeading eyebrow="Evidence" title="What these guests said" align="left" className="mb-10" />
              <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-16">
                {useCase.quotes.map((q) => (
                  <ScrollReveal key={q.name}>
                    <blockquote className="h-full bg-sand border border-sand-deep p-7 flex flex-col">
                      <span className="inline-flex gap-[2px] mb-4" role="img" aria-label="5 out of 5 stars">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className="h-3.5 w-3.5 fill-sun text-sun" aria-hidden="true" />
                        ))}
                      </span>
                      <p className="text-[15px] text-ink-soft font-sans leading-relaxed flex-1">{q.text}</p>
                      <footer className="mt-5 pt-4 border-t border-sand-deep/70 font-serif text-ink text-[16px]">
                        {q.name}
                        {q.location && <span className="text-driftwood text-[13px] font-sans"> · {q.location}</span>}
                      </footer>
                    </blockquote>
                  </ScrollReveal>
                ))}
              </div>

              <p className="text-center text-ink-soft font-sans text-[15px] mb-16">
                All {useCase.reviewCount} are on the{" "}
                <Link href="/reviews" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">guest reviews page</Link>
                , alongside every other stay.
              </p>

              <div className="h-px bg-sand-deep w-full mb-14" />

              <div className="text-center max-w-2xl mx-auto">
                <p className="text-luxury-lg text-ink-soft leading-relaxed mb-6">
                  {useCase.closing.split(" the ")[0]}{" "}
                  {other && (
                    <Link href={`/${other.slug}`} className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">
                      the {other.slug} page
                    </Link>
                  )}{" "}
                  covers the same house from that angle. See the full{" "}
                  <Link href={KUALOA_STAY_PATH} className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">property details</Link>
                  {" "}or read our{" "}
                  <Link href="/policies" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">cancellation policy</Link>.
                </p>
                <Link
                  href={`${KUALOA_STAY_PATH}/book`}
                  data-primary-book-cta
                  className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans tracking-wide px-8 py-4 transition-colors duration-300"
                >
                  Check availability
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
